import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { GED_CONSTANTS } from '../constants/ged.constants';
import { GedListDto } from '../dto/ged/ged-list.dto';
import { GedPositionRequestDto } from '../dto/ged/ged-position/ged-position-request.dto';
import { GedPositionResponseDto } from '../dto/ged/ged-position/ged-position-response.dto';
import { AppConfig } from '../engine/app-config';
import { RestService } from '../engine/http/rest.service';
import { ProgressStatus } from '../enum/progress-status.enum';
import { GedFile } from '../model/ged-file';
import { GedRecord } from '../model/ged-record';
import { CookieService } from '../service/cookie.service';
import { ImageTool } from '../tool/image.tool';

/**
 * Todo :: Queue system to send multiple files cascadingly
 */
@Injectable()
export class GedManager {
  protected readonly restService = inject(RestService);
  protected readonly appConfig = inject(AppConfig);
  protected readonly cookieService = inject(CookieService);

  protected _files$ = new BehaviorSubject<GedFile[]>([]);
  public files$ = this._files$.asObservable();
  public url = '';
  public listUrl = '';
  public recordKey = '';
  public showLoading = false;
  public resizeMaxWidth = GED_CONSTANTS.resizeMaxWidth;
  public resizeMaxHeight = GED_CONSTANTS.resizeMaxHeight;
  public resizeQuality = GED_CONSTANTS.resizeQuality;
  public isResizeEnabled = GED_CONSTANTS.isResizeEnabled;

  public defaultSort = (a: GedFile, b: GedFile) => a.position - b.position;

  protected _isLoadingList = false;
  public get isLoadingList(): boolean {
    return this._isLoadingList;
  }

  protected get endpointList(): string {
    let endpoint = `${this.appConfig.apiEndPoint}/${this.listUrl}`;
    if (this.recordKey) {
      endpoint = `${this.appConfig.apiEndPoint}/${this.listUrl}/${this.recordKey}`;
    }
    return endpoint.replaceAll('//', '/');
  }

  protected get endpoint(): string {
    let endpoint = `${this.appConfig.apiEndPoint}/${this.url}`;
    if (this.recordKey) {
      endpoint = `${this.appConfig.apiEndPoint}/${this.url}/${this.recordKey}`;
    }
    return endpoint.replaceAll('//', '/');
  }

  public applyArgs(args: {
    url: string;
    listUrl: string;
    recordKey?: string;
  }): void {
    this.url = args.url;
    this.listUrl = args.listUrl;
    this.recordKey = args.recordKey;
  }

  public loadList$(): Observable<GedFile[]> {
    this._isLoadingList = true;
    return this.restService.httpClient
      .get<GedListDto[]>(this.endpointList)
      .pipe(
        map((files: GedListDto[]) => {
          const gedFiles = files.map((f) => {
            const file = new GedFile(new GedRecord(f));
            file.endpoint = `${this.appConfig.apiEndPoint}/${this.url}/${f.Id}`;
            file.setCreatedAt(f.CreateDate, f.CreateTime);
            file.position = f.Position;
            return file;
          });
          return gedFiles;
        }),
        tap((gedFiles: GedFile[]) => {
          try {
            gedFiles.sort(this.defaultSort);
          } catch (error) {
            console.error('Error sorting ged files', error);
          }
          gedFiles.forEach((file, index) => {
            if (!Number.isInteger(file.position)) {
              file.position = index;
            }
          });
        }),
        tap((gedFiles: GedFile[]) => {
          this._files$.next([...gedFiles]);
          this._isLoadingList = false;
        }),
      );
  }

  public addFile$(file: File): Observable<boolean> {
    const user = this.cookieService.get('userId');
    const gedFile = new GedFile(file, user);
    return this.addGedFile$(gedFile);
  }

  public addGedFile$(gedFile: GedFile): Observable<boolean> {
    if (this.isResizeEnabled && gedFile.getFile().type.startsWith('image/')) {
      return ImageTool.resizeFile$(
        gedFile.getFile(),
        this.resizeMaxWidth,
        this.resizeMaxHeight,
        this.resizeQuality,
      ).pipe(
        tap((file) => {
          gedFile.setFile(file);
          this.updateFilesAfterAdd(gedFile);
        }),
        map((result) => {
          return !!result;
        }),
      );
    }
    this.updateFilesAfterAdd(gedFile);
    return of(true);
  }

  private updateFilesAfterAdd(gedFile: GedFile): void {
    const files = this._files$.getValue();
    const index = files.findIndex((f) => f.id === gedFile.id);
    gedFile.status = ProgressStatus.notSend;
    if (index < 0) {
      gedFile.position = files.length;
      files.push(gedFile);
      this._files$.next([...files]);
    }
  }

  public sendGedFile$(gedFile: GedFile): Observable<any> {
    return this.restService
      .sendFile(this.endpoint, gedFile, { showLoading: this.showLoading })
      .pipe(
        tap((result) => {
          if (result) {
            gedFile.createdAt = Date.now();
            gedFile.status = ProgressStatus.send;
            gedFile.endpoint = `${this.appConfig.apiEndPoint}/${this.url}/${gedFile.metadata}`;
            this._files$.next([...this._files$.getValue()]);
          }
        }),
      );
  }

  public removeGedFile$(gedFile: GedFile): Observable<boolean> {
    switch (gedFile.status) {
      case ProgressStatus.error:
      case ProgressStatus.notSend:
        this.removeGedFile(gedFile);
        return of(true);
      case ProgressStatus.send:
        gedFile.status = ProgressStatus.waiting;
        return this.restService.removeFile(this.url, gedFile.metadata).pipe(
          map((result) => {
            if (result) {
              this.removeGedFile(gedFile);
              return true;
            }
            return false;
          }),
          catchError(() => {
            gedFile.status = ProgressStatus.error;
            return of(false);
          }),
        );
      default:
        return of(false);
    }
  }

  protected removeGedFile(gedFile: GedFile) {
    const files = this._files$.getValue();
    const index = files.findIndex((f) => f.id === gedFile.id);
    if (index >= 0) {
      files.splice(index, 1);
      this._files$.next([...files]);
    }
  }

  public open(gedFile: GedFile) {
    if (gedFile.metadata) {
      window.open(gedFile.endpoint, '_blank');
      return;
    }
    const objectURL = URL.createObjectURL(gedFile.getFile());
    window.open(objectURL, '_blank');
    URL.revokeObjectURL(objectURL);
  }

  public updatePositionGedFile$(
    gedFile: GedFile,
    pos: number,
  ): Observable<GedFile[]> {
    const data: GedPositionRequestDto = {
      oldPosition: gedFile.position,
      position: pos,
    };
    const url = `${this.url}/${gedFile.metadata}/position`;
    return this.restService.put(url.replaceAll('//', '/'), data).pipe(
      tap((filesPosition: GedPositionResponseDto[]) => {
        const files = this._files$.getValue();
        filesPosition.forEach((fp) => {
          const file = files.find((f) => f.metadata === fp.id);
          if (file) {
            file.position = fp.position;
          }
        });
        this._files$.next([...files].sort((a, b) => a.position - b.position));
      }),
      map(() => this._files$.getValue()),
    );
  }

  public clear() {
    this._files$.next([]);
  }

  public filesHaveChanged(): void {
    this._files$.next([...this._files$.getValue()]);
  }
}
